import { 
  useContext, 
  useRef, 
  useState 
} from "react"

import { 
  DataTable, 
  EllipsisDropdown,
  EmailPreviewModal,
  EmptyPlaceholder, 
  EventMenu,
  EventPageHeader, 
  EventViewsTracker,
  InvitationEmailsWizard,
  Page,
  StudentGuardiansSelectorModal,
} from "@components"

import { 
  ContextualSidebarContext, 
  ModalContext, 
  NotificationContext, 
  UserContext 
} from "@context"

import { 
  ADD_GUARDIANS_TO_INVITATION,
  GET_SES_EMAIL_BATCHES,
  GET_IMPORT_LOG_XLS_DOWNLOAD,
  GET_MIS_GUARDIAN_EMAILS_XLS_DOWNLOAD,
  GET_INVITATIONS_MATRIX,
  GET_INVITATIONS_MATRIX_SUMMARY,
  GET_INVITATIONS_MATRIX_XLS_DOWNLOAD_LINK,
  GET_INVITATION_STUDENT_GUARDIANS,
  GET_PREVIEW_INVITATION_EMAIL,
  REMOVE_GUARDIAN_FROM_EVENT_MUTATION
} from "@graphql"

import { 
  downloadFile, 
  getEventDateRange, 
  getGraphQLErrorMessage, 
  hasExpired, 
  hasFullLiveAccess, 
  mySqlToLocal,
  showLoader
} from "@helpers"

import { 
  useColumnManager,
  useSetActiveSidebarLink, 
  useSetCurrentEvent 
} from "@hooks"

import { 
  useLazyQuery, 
  useMutation, 
  useQuery 
} from "@apollo/client"

import { Button, Chip } from "@heroui/react"
import { EnvelopeOpenIcon } from "@heroicons/react/24/outline"
import { useParams } from "react-router-dom"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import CheckList from "./Checklist"
import Details from "./Details"
import Icons from "./Icons"

/**
 * edit cache to remove deleted guardian
 */
const ManageInvitations = () => {
  const { event: eventId } = useParams()
  const { user } = useContext(UserContext)
  const { setModal } = useContext(ModalContext)
  const { notify } = useContext(NotificationContext)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [studentGuardiansSelectorModal, setStudentGuardiansSelectorModal] = useState({ show: false, isSaving: false, guardians: [], selectedGuardianIds: [], student: null })
  const [isDownloading, setIsDownloading] = useState(false)
  const [invitationPreviewModal, setInvitationPreviewModal] = useState({ id: null, show: false, html: "", title: "" })
  const [isSending, setIsSending] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState([])
  const [removingGuardianId, setRemovingGuardianId] = useState(null)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [refetching, setRefetching] = useState(false)
  const [where, setWhere] = useState(null)
  
  const totalQueuedRef = useRef(null);

  const { data: sesEmailBatchesData } = useQuery(GET_SES_EMAIL_BATCHES, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange : true,
    pollInterval: 15000, // 15 seconds
    skip: isSending === false, // needs to be === as we want to fetch if null
    variables: { 
      input: { 
        event_id: eventId, 
        outstanding_only: true,
        types: ["INVITATION"]
      } 
    },

    onCompleted: (data) => {
      const totalQueued = data.sesEmailBatches.reduce((sum, batch) => sum + batch.no_of_queued, 0)
      setIsSending(totalQueued > 0)

      if (totalQueued !== totalQueuedRef.current) {
        totalQueuedRef.current = totalQueued;
        handleRefetch();
      }
    },

    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while sending updating email stats."), open: true })
    },
  })

  const [downloadImportLogReport, { loading: downloadingImportLogReport }] = useLazyQuery(GET_IMPORT_LOG_XLS_DOWNLOAD, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => downloadFile(data.importLogDownload.filename, data.importLogDownload.url),
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "There was a problem downloading your report. Please try again."), open: true })
  });

  const [downloadMISGuardianEmailsReport, { loading: downloadingMISGuardianEmailsReport }] = useLazyQuery(GET_MIS_GUARDIAN_EMAILS_XLS_DOWNLOAD, {
    variables: { clientId: user.me.client.id },
    fetchPolicy: "no-cache",
    onCompleted: (data) => downloadFile(data.misGuardianEmailsXlsDownload.filename, data.misGuardianEmailsXlsDownload.url),
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "There was a problem downloading your report. Please try again."), open: true })
  });

  const [previewInvitationEmail] = useLazyQuery(GET_PREVIEW_INVITATION_EMAIL, {
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching the invitation preview."), open: true }),
    onCompleted: (data) => setInvitationPreviewModal({ show: true, html: data.previewInvitationEmail.html, title: "Invitation Preview" })
  })

  const [getStudentGuardians] = useLazyQuery(GET_INVITATION_STUDENT_GUARDIANS, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      setStudentGuardiansSelectorModal({ show: true, isLoading: false, guardians: data.student.guardians.filter(guardian => guardian.active), isSaving: false, selectedGuardianIds: data.invitedGuardiansByStudent.map(guardian => guardian.id), student: data.student })
    },
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching student guardians."), open: true }),
  })

  const { data, loading, refetch, networkStatus } = useQuery(GET_INVITATIONS_MATRIX, {
    variables: {
      page,
      perPage,
      input: {
        event_id: eventId,
        search_term: searchTerm,
      },
      orderBy: [
        { column: "STUDENT", order: "ASC" },
        { column: "GUARDIAN", order: "ASC" },
      ],
      where
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
    onError: errors => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching invitations."), open: true })
  })

  const {data: summary, refetch: refetchSummary} = useQuery(GET_INVITATIONS_MATRIX_SUMMARY, {
    variables: {
      event_id: eventId,
    },
    fetchPolicy: "cache-and-network",
  })

  const [addGuardianToEvent] = useMutation(ADD_GUARDIANS_TO_INVITATION, {
    onCompleted: () => {
      refetch().then(() => setStudentGuardiansSelectorModal({ show: false, guardians: [], isSaving: false, selectedGuardianIds: [], student: null }))
      refetchSummary()
    },
    onError: errors => {  
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while adding guardians to the invitation."), open: true })
      setStudentGuardiansSelectorModal(prevState => ({...prevState, isSaving: false }))
    }
  })

  const [removeGuardianFromEvent, { loading: isRemovingGuardian }] = useMutation(REMOVE_GUARDIAN_FROM_EVENT_MUTATION, {
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while removing the guardian from the event."), open: true }),
    onCompleted: (data) => {
      if (data.excludeGuardian) {
        handleRefetch()
        notify({ title: "Success", type: "success", content: "Guardian removed from event successfully.", open: true });
      }
    }
  })

  const [ getInvitationsMatrixXlsDownloadLink ] = useLazyQuery(GET_INVITATIONS_MATRIX_XLS_DOWNLOAD_LINK, {
    variables: { 
      event_id: eventId, 
    },

    fetchPolicy: "network-only",
    
    onCompleted: data => {
      downloadFile(data.invitationsMatrixXlsDownload.filename, data.invitationsMatrixXlsDownload.url)
      setIsDownloading(false)
    },

    onError: errors => {  
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while downloading invitations."), open: true })
      setIsDownloading(false)
    }
  })

  useSetActiveSidebarLink("EVENT_MANAGE_INVITATIONS", "EVENT_MANAGE")
  useSetCurrentEvent(eventId)
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "event.manage.invites.columns", defaultColumns);

  const getSendingStats = (sesEmailBatches) => {
    return sesEmailBatches.reduce((counts, batch) => {
      counts.delivered += batch.no_of_delivered
      counts.failed += batch.no_of_failed
      counts.queued += batch.no_of_queued
      counts.total += batch.no_of_emails
      return counts
    }, { delivered: 0, failed: 0, queued: 0, total: 0 })
  }
  
  const handleAddGuardians = (studentId, guardianIds) => {
    setStudentGuardiansSelectorModal(prevState => ({...prevState, isSaving: true }))

    addGuardianToEvent({
      variables: {
        input: {
          event_id: eventId,
          student_id: studentId,
          guardian_ids: guardianIds,
        }
      }
    })
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getInvitationsMatrixXlsDownloadLink()
    }
  }

  const handleHideInvitationEmailsWizard = () => {
    setModal(prevState => ({ ...prevState, isOpen: false, title: "", content: null }))
  }

  const handleRefetch = () => {
    refetchSummary()
    refetch()
  }

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    refetchSummary()
    refetch().then(() => setRefetching(false))
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleShowStudentGuardianSelectorModal = data => {
    setStudentGuardiansSelectorModal(prevState => ({...prevState, show: true, isLoading: true }))

    getStudentGuardians({ 
      variables: { 
        student_id: data.student_id, 
        invitedGuardiansByStudentInput: { 
          event_id: eventId, 
          student_id: data.student_id 
        } 
      } 
    })
  }

  const handleRemoveGuardianFromEvent = data => {
    setRemovingGuardianId(data.id);

    removeGuardianFromEvent({
      variables: {
        input: {
          invitation_id: data.invitation_id,
          student_id: data.student_id,
          exclude: true,
        }
      }
    }).finally(() => setRemovingGuardianId(null));
  }

  const handleShowInvitationPreviewModal = data => {
    setInvitationPreviewModal(prevState => ({ ...prevState, id: data.id, loading: true }))
    
    previewInvitationEmail({
      variables: { 
        input: { 
          event_id: eventId, 
          user_id: data.user_id 
        } 
      }
    }).finally(() => setInvitationPreviewModal(prevState => ({ ...prevState, loading: false })))
  }

  const handleShowInvitationEmailsWizard = () => {
    setModal(prevState => ({ 
      ...prevState, 
      isOpen: true, 
      title: "Send Invitation Emails", 
      content: <InvitationEmailsWizard 
        event={user.currentEvent} 
        handleClose={() => handleHideInvitationEmailsWizard()}
        handleRefetchMatrix={() => handleRefetch()}
        handleSetIsSending={handleSetIsSending => setIsSending(handleSetIsSending)}
      /> 
    }))
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilter => {
    setSelectedFilters([...selectedFilter])
    selectedFilter.size > 0 ? setWhere(filters[[...selectedFilter][0]].filter) : setWhere(null)
    setPage(1)
  }

  const renderCell = (item, column) => {    
    const remainingGuardiansForSelection = item.total_available_guardians - item.total_selected_guardians

    const remainingGuardianCounts = (
      <span>
        {item.guardian} 
        <Chip color="secondary" classNames={{ base: "ml-2", content: "font-semibold" }} size="sm">
          +{remainingGuardiansForSelection}
        </Chip>
      </span>
    )
    
    const isLoading = 
      (item.id === invitationPreviewModal.id && invitationPreviewModal.loading) || 
      (item.id === removingGuardianId && isRemovingGuardian);

      const columnMapping = {
        student: <span className="font-semibold text-content2-foreground">{item.student}</span>,
        guardian: remainingGuardiansForSelection > 0 ?  remainingGuardianCounts : item.guardian,
        email: item.email,
        icons: <Icons 
          activatedAt={item.activated_at} 
          contactSequence={item.contact_sequence} 
          legalGuardian={item.legal_guardian} 
          lastEmailErrored={item.last_email_errored}
          lastEmailQueued={item.last_email_queued}
          lastEmailSent={item.last_email_sent}
          status={item.status} 
        />,
        emailed_date: item.last_sent_at ? mySqlToLocal(item.last_sent_at).format("DD/MM/YY HH:mm") : "-",
        actions: <EllipsisDropdown isLoading={isLoading} items={[
          { id: 1, handleClick: () => handleShowStudentGuardianSelectorModal(item), caption: "Add Guardian(s)", isDisabled: isReadOnlyMode },
          { id: 2, handleClick: () => handleRemoveGuardianFromEvent(item), caption: "Remove Guardian", isDisabled: isReadOnlyMode },
          { 
            id: 3, 
            handleClick: () => setContextualSidebar(prevState => ({
              ...prevState,
              open: true,
              title: "Invitation Details",
              subTitle: `Invitation details are listed below.`,
              content: (
                <Details 
                  invitation={item} 
                  event={user.currentEvent} 
                  isReadOnly={isReadOnlyMode} 
                  handlePollForSendingEmails={(value) => setIsSending(value)} 
                />
              ),
              size: "lg",
            })),    
            caption: "More Details" 
          },
          { id: 4, handleClick: () => handleShowInvitationPreviewModal(item), caption: "Preview Invitation" },
        ]} 
      />
    }
      
    return columnMapping[column]
  }

  const invitationsMatrix = data?.invitationsMatrix?.data
  const { ends } = getEventDateRange(user.currentEvent.sessions)
  const isReadOnlyMode = hasExpired(ends)
  const isSendEmailsDisabled = isSending === null || isSending === true || isReadOnlyMode
  const sendingStats = getSendingStats(sesEmailBatchesData?.sesEmailBatches || [])
  const showLoadingPlaceholder = showLoader(loading, networkStatus, invitationsMatrix, refetching)

  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Manage Invitations"
          icon={<EnvelopeOpenIcon className="page-header-icon" />}
          event={user?.currentEvent}
          actions={hasFullLiveAccess() && <EventMenu event={user?.currentEvent} user={user} />}
        />

        <CheckList
          event={user?.currentEvent}
          handleViewUnmatched={() => handleToggleFilter(new Set(["unmatched"])) }
          handleSendOutstandingEmails={() => handleShowInvitationEmailsWizard({})}
          noOfMatched={summary?.invitationsMatrixSummary?.matched} 
          noOfUnmatched={summary?.invitationsMatrixSummary?.unmatched} 
          noOfEmailsSent={summary?.invitationsMatrixSummary?.emailed} 
          noOfEmailsNotSent={summary?.invitationsMatrixSummary?.not_emailed}
          noOfStudents={summary?.invitationsMatrixSummary?.students}
          isReadOnly={isReadOnlyMode}
          isSendEmailsDisabled={isSendEmailsDisabled}
          sendingStats={sendingStats}      
        />
        
        <DataTable
          actions={
            <Button 
              onPress={() => handleShowInvitationEmailsWizard({})} 
              isDisabled={isSendEmailsDisabled}
              color="success" 
              className="font-semibold text-white shrink-0"
            >
              Send Emails
            </Button>
          }
          columns={columns}
          currentPage={page}
          data={invitationsMatrix}
          disallowEmptyFilterSelection={false}
          downloadOptions={downloadOptions}
          filters={filters}
          filtersSelectionMode="single"
          handleDownload={type => handleDownload(type)}
          handleRefetch={() => handleRefetchButtonClicked()}
          handlePageChange={page => setPage(page)}
          handleRenderCell={(item, key) => renderCell(item, key)}
          handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
          handleSearchClear={() => setSearchTerm("")}
          handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
          handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
          isDownloading={isDownloading}
          loading={showLoadingPlaceholder}
          isRefetching={refetching}
          noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Invitations Found" description="No invitations found! Try clearing any filters." />}
          paginatorInfo={data?.invitationsMatrix?.paginatorInfo}
          searchInputPlaceHolder="Search by student / guardian / email"
          searchTerm={searchTerm}
          selectedColumns={selectedColumns}
          selectedFilters={selectedFilters}
          showColumnsButton={true}
          showDownloadButton={true}
          showFilterCount={true}
          showRefetchButton={true}
        />

        {studentGuardiansSelectorModal.show && (
          <StudentGuardiansSelectorModal
            guardians={studentGuardiansSelectorModal.guardians}
            handleClose={() => setStudentGuardiansSelectorModal({ show: false, isSaving: false, guardians: [], selectedGuardianIds: [], student: null })}
            handleDownloadImportLogReport={() => downloadImportLogReport({ variables: { input: { client_id: user.me.client.id } } }) }
            handleDownloadMISGuardianEmailsReport={() => downloadMISGuardianEmailsReport()}
            handleSave={(studentId, guardianIds) => handleAddGuardians(studentId, guardianIds)}
            isDownloadingImportLogReport={downloadingImportLogReport}
            isDownloadingMISGuardianEmailsReport={downloadingMISGuardianEmailsReport}
            isLoading={studentGuardiansSelectorModal.isLoading}
            isSaving={studentGuardiansSelectorModal.isSaving}
            selectedGuardianIds={studentGuardiansSelectorModal.selectedGuardianIds}
            student={studentGuardiansSelectorModal.student}
            show={studentGuardiansSelectorModal.show}
            title="Select the guardians you would like to add to the event."
          />
        )}

        {invitationPreviewModal.show && (
          <EmailPreviewModal 
            handleClose={() => setInvitationPreviewModal(prev => ({ ...prev, show: false }))}
            html={invitationPreviewModal?.html} 
            show={invitationPreviewModal.show}
            title="Invitation Preview"
          />
        )}
      </Page>
    </EventViewsTracker>
  )
}

export default ManageInvitations
